import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { POLICIES_QUERY } from '@/modules/policy/api/queryKeys';
import type { Label, LabelScope } from '@@/packages/shared/types/models/label';
import { AVAILABLE_LABELS_QUERY } from './queryKeys';
import { parseLabel, type RawLabel } from './parseLabel';

type CreateLabelVariables = {
  name: string;
  description: string;
  colour: string;
  scope: LabelScope;
};

type UpdateLabelVariables = {
  id: string;
  name: string;
  description: string;
  colour: string;
};

function useAvailableLabelsQuery(options?: QueryOptions) {
  return useQuery(
    mergeQueryOptions(options, {
      queryKey: [AVAILABLE_LABELS_QUERY],
      async queryFn() {
        const res = await request<RawLabel[]>('GET', `/labels`);
        return res.data.map(parseLabel);
      },
    }),
  );
}

export function useAvailableLabels(
  scope?: MaybeRefOrGetter<LabelScope | undefined>,
  options?: QueryOptions,
) {
  const query = useAvailableLabelsQuery(options);

  const labelList = computed(() => {
    const labels = query.data.value || [];
    const wanted = toValue(scope);
    return wanted ? labels.filter((label) => label.scope === wanted) : labels;
  });
  const labelsByID = computed(() =>
    Object.fromEntries(labelList.value.map((label) => [label.id, label])),
  );

  return { ...query, query, labelList, labelsByID };
}

export function useCreateLabel(options?: MutationOptions<Label, CreateLabelVariables>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(label) {
        const res = await request<RawLabel>('POST', '/labels', label);
        return parseLabel(res.data);
      },
      async onSuccess(label) {
        await queryClient.setQueryData([AVAILABLE_LABELS_QUERY], (oldData: Label[] | undefined) => {
          if (!oldData) return undefined;
          return oldData.concat(label) satisfies Label[];
        });
        await queryClient.invalidateQueries({ queryKey: [POLICIES_QUERY] });
      },
    }),
  );
}

export function useUpdateLabel(options?: MutationOptions<Label, UpdateLabelVariables>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(label) {
        const res = await request<RawLabel>('PUT', `/labels/${label.id}`, label);
        return parseLabel(res.data);
      },
      async onSuccess(newLabel) {
        await queryClient.setQueryData([AVAILABLE_LABELS_QUERY], (oldData: Label[] | undefined) => {
          if (!oldData) return undefined;
          return oldData.map((label) =>
            label.id === newLabel.id ? newLabel : label,
          ) satisfies Label[];
        });
        await queryClient.invalidateQueries({ queryKey: [POLICIES_QUERY] });
      },
    }),
  );
}

export function useDeleteLabel(options?: MutationOptions<Label, string>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(id) {
        const res = await request('DELETE', `/labels/${id}`);
        return res.data;
      },
      async onSuccess(_data, id) {
        await queryClient.setQueryData([AVAILABLE_LABELS_QUERY], (oldData: Label[] | undefined) => {
          if (!oldData) return undefined;
          return oldData.filter((label) => label.id !== id) satisfies Label[];
        });
        await queryClient.invalidateQueries({ queryKey: [POLICIES_QUERY] });
      },
    }),
  );
}
